"use client";

import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

type Props = { className?: string };

const IMAGES = ['/hero2.jpg', '/design-process-image-2.jpg'];

export default function HeroSlider({ className = '' }: Props) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    const gap = 4000;
    if (!paused) {
      intervalRef.current = window.setInterval(() => {
        setIndex((prev) => (prev + 1) % IMAGES.length);
      }, gap);
    }
    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
    };
  }, [paused]);

  const next = () => {
    setIndex((s) => (s + 1) % IMAGES.length);
    setPaused(true);
    if (intervalRef.current) window.clearInterval(intervalRef.current);
    setTimeout(() => setPaused(false), 6000);
  };
  
  const prev = () => {
    setIndex((s) => (s - 1 + IMAGES.length) % IMAGES.length);
    setPaused(true);
    if (intervalRef.current) window.clearInterval(intervalRef.current);
    setTimeout(() => setPaused(false), 6000);
  };

  return (
    <div
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slides container */}
      <div
        style={{
          display: 'flex',
          height: '100%',
          width: '100%',
          transform: `translateX(-${index * 100}%)`,
          transition: 'transform 600ms ease-in-out',
        }}
      >
        {IMAGES.map((src, i) => (
          <div
            key={i}
            style={{
              flex: '0 0 100%',
              height: '100%',
              width: '100%',
              position: 'relative',
            }}
          >
            <img
              src={src}
              alt={`Interior design ${i + 1}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </div>
        ))}
      </div>

      {/* Gradient overlays */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to right, rgba(0,0,0,0.6), rgba(0,0,0,0.4), transparent)',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)',
          pointerEvents: 'none',
        }}
      />

      {/* Navigation buttons */}
      <button
        onClick={prev}
        aria-label="Previous"
        style={{
          position: 'absolute',
          left: '18px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.5)',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 30,
        }}
      >
        <Icon name="ChevronLeftIcon" size={20} />
      </button>
      
      <button
        onClick={next}
        aria-label="Next"
        style={{
          position: 'absolute',
          right: '18px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.5)',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 30,
        }}
      >
        <Icon name="ChevronRightIcon" size={20} />
      </button>

      {/* Dots */}
      <div
        style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '8px',
          zIndex: 30,
        }}
      >
        {IMAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            aria-label={`Go to slide ${i + 1}`}
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: i === index ? '#fff' : 'rgba(255,255,255,0.5)',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          />
        ))}
      </div>
    </div>
  );
}
